import React, { useState, useEffect, useRef } from "react";
import { useProfileApi } from "../Services/api/profileApi";
import { useMomApi } from "../Services/api/momApi";
import { useBalanceSheetApi } from "../Services/api/balanceSheetApi";
import { useCommentsApi } from "../Services/api/commentsApi";
import { useDownloadApi } from "../Services/api/downloadApi";
import useLocalStorage from "../hooks/useLocalStorage";
import { useLoader } from "../components/common/LoaderContext";
import { useAppContext } from "../hooks/context/AppContext";
import { errorToast, successToast, warningToast } from "../Utils/toast";
import { AuditData, TableRowData, TotalAmountData } from "../types/table";
import { useDropdowns } from "../hooks/useDropdowns";
import { AdvancedFilters } from "../types/filters";
import { buildParams } from "../Utils/buildParams";
import { useQueryParams } from "../hooks/useQueryParams";
import { useHeader } from "../hooks/context/HeaderContext";
import PrimaryLandscapeSignOff from "./PrimaryLandscapeSignOff";
// import SecondaryLandscapeSignOff from "./SecondaryLandscapeSignOff";

export function convertKeysToString(input: any) {
  return input.map((item: any) => {
    return {
      key: item.key.toString(),
      value: item.value,
    };
  });
}

const SignOffPage: React.FC = () => {
  const { updateHeaderInfo } = useHeader();
  const { getProfile } = useProfileApi();
  const { getMomData } = useMomApi();
  const { getBalanceSheetResult } = useBalanceSheetApi();
  const { savePaginatedComments, saveDraft, submitForReview } =
    useCommentsApi();
  const { downloadCsv } = useDownloadApi();
  const { loading, setLoading } = useLoader();
  const [userInfo, setUserInfo] = useLocalStorage("user_info", null);
  const [isValidUser, setisValidUser] = useState(true);
  const [idToken, setIdToken] = useState(null);
  const [, setAuthData] = useLocalStorage("auth_data", null);
  const signOffComment =
    "OC has reviewed all the accounts that are under “Review Required” as per the established threshold.Threshold Confirmation: The threshold limit is based on the country controller’s confirmation and is in line with control requirements";
  const [signOffComments, setSignOffComments] =
    useState<string>(signOffComment);
  const [signOffError, setSignOffError] = useState<string>("");
  const { submitSignOff } = useCommentsApi(); // <-- Make sure this exists in your API hooks

  const [tableLoading, setTableLoading] = useState(false);
  const [order, setOrder] = useState<string>("asc");
  const [orderBy, setOrderBy] = useState("REPORTING_GROUP");
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const { userData, updateUserData, authData } = useAppContext();
  const { getQueryParams } = useQueryParams();
  const queryParams = getQueryParams();
  const landscapeId =
    queryParams.LandscapeID === ""
      ? userData?.LandscapeInfo && userData.LandscapeInfo.length > 0
        ? userData.LandscapeInfo[0]?.LandscapeId
        : undefined
      : queryParams.LandscapeID;
  const CBTType = queryParams.CBTType || "";
  //effect to only set the landscape name in the header
  useEffect(() => {
    const selectedLandscape = userData.LandscapeInfo?.find(
      (l: any) => String(l.LandscapeId) === String(landscapeId)
    );
    updateHeaderInfo({
      landscapeName: selectedLandscape ? selectedLandscape.LandscapeName : "",
    });
  }, [landscapeId]);

  const handleFilterChangeWithWarning = (
    key: keyof AdvancedFilters,
    value: string | string[]
  ) => {
    setFiltersChanged(true);
    if (commentsArray.length > 0) {
      // setPendingChange({ key, value });
      setShowWarningModal(true);
      return false;
    }
    // Proceed with the change
    return true;
  };
  const [currency, setCurrency] = useState("LC"); // Local Currency
  const { filters, dropdownOptions, thresholdValues, handleFilterChange } =
    useDropdowns(userData, handleFilterChangeWithWarning, setCurrency);
  const [currencyMode, setCurrencyMode] = useState("local");
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [cbtTypeOptions, setCbtTypeOptions] = useState<any[]>([
    {
      CBTTypes_Data: "",
    },
  ]);
  const [companyCodeOptions, setCompanyCodeOptions] = useState<any[]>([
    {
      CompanyCode: "",
    },
  ]);
  const [reportingGroupOptions, setReportingGroupOptions] = useState<any[]>([
      {
        ReportingGroup: "",
      },
    ]);
  const [L3CodeOptions, setL3CodeOptions] = useState<any[]>([
    {
      L3Code: "",
    },
  ]);
  const [cbtFilter, setCbtFilter] = useState<string>("All");
  const [companyFilter, setCompanyFilter] = useState<string>("All");
  const [rgFilter, setRgFilter] = useState<string>("All");
  const [glFilter, setGlFilter] = useState<string>("All");
  const [auditData, setAuditData] = useState<AuditData[]>([
    {
      ValidationMessage: "",
      EnablePage: true,
      SubmittedBy: "",
      SubmittedDate: "",
      SignOffBy: "",
      SignOffDate: "",
      TotalRecords: 0,
    },
  ]);
  const [totalAmountData, setTotalAmountData] = useState<TotalAmountData[]>([
    {
      TotalReportingValue: 0,
      TotalComparisonValue: 0,
    },
  ]);
  const [page, setPage] = useState(0);
  const [isSignedOff, setIsSignedOff] = useState(false);
  const [openDraftDialog, setOpenDraftDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState<"signoff" | "savecomments" | null>(null);
  const [openMomDialog, setOpenMomDialog] = useState(false);
  const [momData, setMomData] = useState([
    { MOM: "", SubmittedBy: "", submittedDate: "" },
  ]);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, boolean>
  >({
    marketvalue: false,
    mco: false, // Start with true since initially empty
    country: false,
    reportingyear: false,
    comparisonyear: false,
    reviewscenario: false,
    companycodes: false,
    reportingmonth: false,
    comparisonmonth: false,
  });
  const [commentsArray, setCommentsArray] = useState<number[]>([]);

  const validateAndFetchTableData = (extraFilters?: {
    cbtFilter?: string;
    companyFilter?: string;
    glFilter?: string;
    rgFilter?: string;
  }) => {
    const hasRequiredFilters =
      filters.country &&
      filters.companycodes &&
      filters.companycodes.length > 0 &&
      filters.mco &&
      filters.marketvalue &&
      filters.reviewscenario;
    if (hasRequiredFilters) {
      fetchTableData(extraFilters);
    }
  };
  useEffect(() => {
    const extraFilters: {
      cbtFilter?: string;
      companyFilter?: string;
      glFilter?: string;
      rgFilter?: string;
    } = {};
    if (cbtFilter && cbtFilter !== "All") extraFilters.cbtFilter = cbtFilter;
    if (companyFilter && companyFilter !== "All")
      extraFilters.companyFilter = companyFilter;
    if (glFilter && glFilter !== "All") extraFilters.glFilter = glFilter;
    if (rgFilter && rgFilter !== "All") extraFilters.rgFilter = rgFilter;
    if (commentsArray.length > 0) {
      saveComments("Pagination", extraFilters);
    } else {
      validateAndFetchTableData(extraFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, currencyMode, order, orderBy]);

  useEffect(() => {
    const fetchMomData = async () => {
      const params = {
        ...buildParams(
          filters,
          userData,
          currency,
          page,
          order,
          orderBy,
          landscapeId,
          CBTType,
          currencyMode
        ),
        CurrencyCode: currencyMode === "euro" ? "euro" : currency,
        ReviewScenarioID: filters.reviewscenario,
        SortByColumnKey: orderBy,
        SortOrder: orderBy,
        CBTType: CBTType || "",
        SearchCBTType: cbtFilter || "ALL",
        SearchCompanyCode: companyFilter || "ALL",
        SearchL3Code: glFilter || "ALL",
        SearchReportingGroup: rgFilter || "ALL",
        TypeOfAction: "GO",
      };
      console.log(params, "params");
      if (tableData.length > 0) {
        const response = await getMomData(params);
        if (response) {
          setMomData(response);
        }
      }
    };
    fetchMomData();
  }, [openMomDialog]);

  const fetchTableData = async (extraFilters?: {
    cbtFilter?: string;
    companyFilter?: string;
    glFilter?: string;
    rgFilter?: string;
  }) => {
    setLoading(true);
    const cbtVal = extraFilters?.cbtFilter ?? "All";
    const companyVal = extraFilters?.companyFilter ?? "All";
    const glVal = extraFilters?.glFilter ?? "All";
    const rgVal = extraFilters?.rgFilter ?? "All";
    const extendedParams = {
      ...buildParams(
        filters,
        userData,
        currency,
        page,
        order,
        orderBy,
        landscapeId,
        CBTType,
        currencyMode
      ),
      PageNumber: page + 1,
      PerPageCount: 100,
      TypeOfAction: "GO",
      CountryCode: filters.country,
      CompanyCodes: Array.isArray(filters.companycodes)
        ? filters.companycodes.join(",")
        : filters.companycodes || "",
      SearchCBTType: cbtVal,
      SearchCompanyCode: companyVal,
      SearchL3Code: glVal,
      SearchReportingGroup: rgVal,
    };
    let response = await getBalanceSheetResult(extendedParams);
    if (response) {
      const auditItem =
        Array.isArray(response.Paginated_AuditData) &&
        response.Paginated_AuditData.length > 0
          ? response.Paginated_AuditData[0]
          : undefined;
      setTableData(response.Data);
      setAuditData(response.Paginated_AuditData);
      setCbtTypeOptions(response.CBTTypes_Data);
      setCompanyCodeOptions(response.CompanyCode_Data);
      setL3CodeOptions(response.L3Code_Data);
      setReportingGroupOptions(response.L3Description_Data);
      setTotalAmountData(response.TotalAmount_Data);
      if (auditItem.SignOffComments !== null) {
        setSignOffComments(auditItem.SignOffComments);
      } else {
        setSignOffComments(signOffComment);
      }
      if (auditItem.EnablePage) {
        setIsSignedOff(false);
      } else {
        setIsSignedOff(true);
      }
      // Update header info after data is fetched
      updateHeaderInfo({
        submittedBy: auditItem.SubmittedBy,
        submittedDate: auditItem.SubmittedDate,
        signOffBy: auditItem.SignOffBy,
        signOffDate: auditItem.SignOffDate,
        commentStatus: auditItem.CommentsStatus,
        refreshDate: auditItem.RefreshDate,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (authData.idToken) {
      setLoading(true);
      fetchUserProfile();
    }
  }, [authData]);

  const handleTableFilterChange = (
    value: string,
    filterType: "cbtFilter" | "companyFilter" | "glFilter" | "rgFilter"
  ) => {
    switch (filterType) {
      case "cbtFilter":
        setCbtFilter(value);
        break;
      case "companyFilter":
        setCompanyFilter(value);
        break;
      case "glFilter":
        setGlFilter(value);
        break;
      case "rgFilter":
        setRgFilter(value);
        break;

      default:
        break;
    }
  };
  useEffect(() => {
    const extraFilters: {
      cbtFilter?: string;
      companyFilter?: string;
      glFilter?: string;
      rgFilter?: string;
    } = {};
    if (cbtFilter) extraFilters.cbtFilter = cbtFilter;
    if (companyFilter) extraFilters.companyFilter = companyFilter;
    if (glFilter) extraFilters.glFilter = glFilter;
    if (rgFilter) extraFilters.rgFilter = rgFilter;

    if (
      Object.keys(extraFilters).length > 0 &&
      companyCodeOptions[0].CompanyCode.length > 0
    ) {
      fetchTableData(extraFilters);
    }
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cbtFilter, companyFilter, glFilter, rgFilter]);

  // Fetch user profile and update user data
  // This function is called when the component mounts or when authData changes
  let fetchUserProfile = async () => {
    setLoading(true);
    try {
      let res = await getProfile();
      if (res) {
        setUserInfo(JSON.stringify(res));
        updateUserData(res);
      }
    } catch (error) {
      setisValidUser(false);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle the click of the save the paginated comments
  const saveComments = async (
    TypeOfAction: string,
    extraFilters: {
      cbtFilter?: string;
      companyFilter?: string;
      glFilter?: string;
    }
  ) => {
    setLoading(true);
    const arr = tableData.map((row) => {
      return {
        CompanyId: row?.CompanyID || 0,
        CommentsID: row?.CommentsID || 0,
        Comments: row?.Comments || "",
      };
    });
    const extendedParams = {
      ...buildParams(
        filters,
        userData,
        currency,
        page,
        order,
        orderBy,
        landscapeId,
        CBTType,
        currencyMode
      ),
      TypeOfAction: TypeOfAction,
      Comments: arr,
    };
    if (tableData.length > 0) {
      try {
        const response = await savePaginatedComments(extendedParams);
        if (response) {
          setCommentsArray([]);
          successToast("Comments saved successfully!");
          validateAndFetchTableData(extraFilters);
        }
      } catch (error) {
        errorToast("Error saving comments.");
        console.error("Error saving comments:", error);
      }
    } else {
      errorToast("No data available to save comments.");
    }
    setLoading(false);
  };

  const handleClick = async () => {
    setFiltersChanged(false);
    setCbtFilter("All");
    setCompanyFilter("All");
    setGlFilter("All");
    setRgFilter("All");
    const newValidationErrors = {
      mco: !filters.mco || filters.mco === "",
      country: !filters.country || filters.country === "",
      reportingyear: !filters.reportingyear,
      comparisonyear: !filters.comparisonyear,
      reviewscenario: !filters.reviewscenario || filters.reviewscenario === "",
      companycodes:
        !filters.companycodes ||
        (Array.isArray(filters.companycodes) &&
          filters.companycodes.length === 0),
      reportingmonth: filters.reportingmonth.toString() === "",
      comparisonmonth: filters.comparisonmonth.toString() === "",
    };

    setValidationErrors(newValidationErrors);
    setPage(0);
    const validateClick = Object.values(newValidationErrors).some(
      (item) => item
    );
    if (validateClick) {
      return;
    }
    try {
      setLoading(true);
      const response = await getBalanceSheetResult({
        ...buildParams(
          filters,
          userData,
          currency,
          page,
          order,
          orderBy,
          landscapeId,
          CBTType,
          currencyMode
        ),
        TypeOfAction: "GO",
      });
      const auditItem =
        Array.isArray(response.Paginated_AuditData) &&
        response.Paginated_AuditData.length > 0
          ? response.Paginated_AuditData[0]
          : undefined;
      setTableData(response.Data);
      setAuditData(response.Paginated_AuditData);
      setCbtTypeOptions(response.CBTTypes_Data);
      setCompanyCodeOptions(response.CompanyCode_Data);
      setReportingGroupOptions(response.L3Description_Data);
      setL3CodeOptions(response.L3Code_Data);
      setTotalAmountData(response.TotalAmount_Data);
      if (auditItem.SignOffComments !== null) {
        setSignOffComments(auditItem.SignOffComments);
      } else {
        setSignOffComments(signOffComment);
      }
      if (auditItem.EnablePage) {
        setIsSignedOff(false);
      } else {
        setIsSignedOff(true);
      }
      updateHeaderInfo({
        submittedBy: auditItem.SubmittedBy,
        submittedDate: auditItem.SubmittedDate,
        signOffBy: auditItem.SignOffBy,
        signOffDate: auditItem.SignOffDate,
        commentStatus: auditItem.CommentsStatus,
        refreshDate: auditItem.RefreshDate,
      });
    } catch (error) {
      console.error("Failed to fetch table data:", error);
      errorToast("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const closeWarningModal = () => {
    setShowWarningModal(false);
  };
  const clearComments = () => {
    const extraFilters: {
      cbtFilter?: string;
      companyFilter?: string;
      glFilter?: string;
      rgFilter?: string;
    } = {};
    if (cbtFilter) extraFilters.cbtFilter = cbtFilter;
    if (companyFilter) extraFilters.companyFilter = companyFilter;
    if (glFilter) extraFilters.glFilter = glFilter;
    if (rgFilter) extraFilters.rgFilter = rgFilter;
    setCommentsArray([]);
    closeWarningModal();
    fetchTableData(extraFilters);
  };
  // Function to save comments from sign-off page (PageType: 2)
  const saveCommentsSignOff = async () => {
    setLoading(true);
    const arr = tableData.map((row) => ({
      CompanyId: row?.CompanyID || 0,
      CommentsID: row?.CommentsID || 0,
      Comments: row?.Comments || "",
      OldComments: row?.OldComments || "",
    }));
    const extendedParams = {
      ...buildParams(
        filters,
        userData,
        currency,
        page,
        order,
        orderBy,
        landscapeId,
        CBTType,
        currencyMode
      ),
      TypeOfAction: "SAVE_AS_DRAFT",
      PageType: 2,
      Comments: arr,
    };
    if (tableData.length > 0) {
      try {
        const response = await saveDraft(extendedParams);
        if (response) {
          setCommentsArray([]);
          successToast("Comments saved successfully!");
          validateAndFetchTableData();
        }
      } catch (error) {
        errorToast("Error saving comments.");
        console.error("Error saving comments:", error);
      }
    } else {
      errorToast("No data available to save comments.");
    }
    setLoading(false);
  };

  // Function to handle the sign-off action
  const handleSignOff = async () => {
    if (!signOffComments || !signOffComments.trim()) {
      setSignOffError("Sign-Off Comments cannot be empty.");
      errorToast("Sign-Off Comments cannot be empty.");
      return;
    }
    setSignOffError("");
    try {
      setLoading(true);
      const arr = tableData.map((row) => {
        return {
          CompanyId: row?.CompanyID || 0,
          CommentsID: row?.CommentsID || 0,
          Comments: row?.Comments || "",
        };
      });
      const extendedParams = {
        ...buildParams(
          filters,
          userData,
          currency,
          page,
          order,
          orderBy,
          landscapeId,
          CBTType,
          currencyMode
        ),
        TypeOfAction: "SUBMIT_FOR_REVIEW",
        Comments: arr,
      };
      const submitResponse = await submitForReview(extendedParams);
      if (submitResponse) {
        setCommentsArray([]);
        if (submitResponse.Data.length === 0) {
          const response = await submitSignOff({
            UserID: userData.UserID,
            userEmail: userData?.Email,
            LandscapeID: landscapeId,
            MarketValue: filters.marketvalue,
            MCO: filters.mco,
            CountryCode: filters.country,
            CountryCodeName: filters.country,
            CompanyCodes: Array.isArray(filters.companycodes)
              ? filters.companycodes.join(",")
              : filters.companycodes,
            BSReportingYear: filters.reportingyear,
            BSReportingMonth: filters.reportingmonth,
            BSComparisonYear: filters.comparisonyear,
            BSComparisonMonth: filters.comparisonmonth,
            PLReportingYear: filters.plreportingyear,
            PLReportingMonth: filters.plreportingmonth,
            PLComparisonYear: filters.plcomparisonyear,
            PLComparisonMonth: filters.plcomparisonmonth,
            PageType: 2,
            SignOffComments: signOffComments,
            CBTType: CBTType,
            ReviewScenarioID: filters.reviewscenario,
            CurrencyCode: currency,
          });
          if (response) {
            successToast(response.message);
            validateAndFetchTableData();
          } else {
          }
        } else {
          setAuditData(submitResponse.Paginated_AuditData);
          setTableData(submitResponse.Data);
        }
      }
    } catch (error) {
      errorToast("Sign-Off failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
    console.log("reportingGroupOptions page:", reportingGroupOptions);

  const renderByLandscape = () => {
     console.log("reportingGroupOptions page:", reportingGroupOptions);

    return (
      <PrimaryLandscapeSignOff
        openDraftDialog={openDraftDialog}
        setOpenDraftDialog={setOpenDraftDialog}
        openMomDialog={openMomDialog}
        setOpenMomDialog={setOpenMomDialog}
        dialogAction={dialogAction}
        showWarningModal={showWarningModal}
        setShowWarningModal={setShowWarningModal}
        closeWarningModal={closeWarningModal}
        clearComments={clearComments}
        setDialogAction={setDialogAction}
        validationErrors={validationErrors}
        filters={filters}
        dropdownOptions={dropdownOptions}
        handleFilterChange={handleFilterChange}
        currencyMode={currencyMode}
        setCurrencyMode={setCurrencyMode}
        handleClick={handleClick}
        thresholdValues={thresholdValues}
        currency={currency}
        auditData={auditData}
        totalAmountData={totalAmountData}
        isSignedOff={isSignedOff}
        signOffComments={signOffComments}
        setSignOffComments={setSignOffComments}
        handleSignOff={handleSignOff}
        saveCommentsSignOff={saveCommentsSignOff}
        tableData={tableData}
        setTableData={setTableData}
        setPage={setPage}
        page={page}
        commentsArray={commentsArray}
        setCommentsArray={setCommentsArray}
        tableLoading={tableLoading}
        filtersChanged={filtersChanged}
        setFiltersChanged={setFiltersChanged}
        order={order}
        orderBy={orderBy}
        setOrder={setOrder}
        setOrderBy={setOrderBy}
        userData={userData}
        landscapeId={landscapeId}
        CBTType={CBTType}
        downloadCsv={downloadCsv}
        cbtTypeOptions={cbtTypeOptions}
        companyCodeOptions={companyCodeOptions}
        L3CodeOptions={L3CodeOptions}
        cbtFilter={cbtFilter}
        setCbtFilter={setCbtFilter}
        companyFilter={companyFilter}
        setCompanyFilter={setCompanyFilter}
        glFilter={glFilter}
        setGlFilter={setGlFilter}
        rgFilter={rgFilter}
        setRgFilter={setRgFilter}
        reportingGroupOptions={reportingGroupOptions}
        handleTableFilterChange={handleTableFilterChange}
        momData={momData} 
      />
    );
    return null;
  };
  return <>{renderByLandscape()}</>;
};

export default SignOffPage;